import { useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useSubmitFeedbackMutation,
  useGetMyFeedbackQuery,
  useDeleteMyFeedbackMutation,
} from '@/redux/feedback/feedback.api.slice';
import type { FeedbackCategory } from '@/redux/feedback/feedback.d';

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: 'Bug report' },
  { value: 'feature', label: 'Feature request' },
  { value: 'idea', label: 'Idea' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: 'Bug report',
  feature: 'Feature request',
  idea: 'Idea',
  other: 'Other',
};

function formatDate(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function FeedbackModal({
  open,
  onOpenChange,
  initialCategory = 'feature',
  initialSubject = '',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCategory?: FeedbackCategory;
  initialSubject?: string;
}) {
  const [tab, setTab] = useState('submit');
  const [category, setCategory] = useState<FeedbackCategory>(initialCategory);
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState('');
  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
  const { data: myFeedback = [], isLoading: isLoadingMine } =
    useGetMyFeedbackQuery(undefined, { skip: !open });
  const [deleteMyFeedback, { isLoading: isDeleting }] =
    useDeleteMyFeedbackMutation();

  const handleDelete = async (id: string) => {
    try {
      await deleteMyFeedback(id).unwrap();
      toast.success('Feedback deleted');
    } catch {
      toast.error('Could not delete feedback');
    }
  };

  const reset = () => {
    setCategory(initialCategory);
    setSubject(initialSubject);
    setMessage('');
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      reset();
      setTab('submit');
    }
    onOpenChange(next);
  };

  // Submit guard branches + try/catch error mapping; inherently branchy.
  // fallow-ignore-next-line complexity
  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    try {
      await submitFeedback({
        category,
        subject: subject.trim() || undefined,
        message: message.trim(),
      }).unwrap();
      toast.success('Thanks for your feedback!', {
        description: 'Track its status under "My feedback".',
      });
      reset();
      setTab('mine');
    } catch (error) {
      const description =
        (error as { data?: { message?: string } })?.data?.message ??
        'Please try again in a moment.';
      toast.error('Could not send feedback', { description });
    }
  };

  const renderMine = () => {
    if (isLoadingMine) {
      return (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Loading…
        </p>
      );
    }
    if (myFeedback.length === 0) {
      return (
        <p className="text-sm text-muted-foreground py-8 text-center">
          You haven&apos;t sent any feedback yet.
        </p>
      );
    }
    return (
      <ul className="space-y-3">
        {myFeedback.map((item) => (
          <li key={item._id} className="rounded-lg border border-[#D6D6D6] p-3">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline">{CATEGORY_LABELS[item.category]}</Badge>
              <Badge
                variant={item.status === 'resolved' ? 'default' : 'secondary'}
              >
                {item.status === 'resolved' ? 'Resolved' : 'Open'}
              </Badge>
              <span className="ml-auto text-xs text-muted-foreground">
                {formatDate(item.createdAt)}
              </span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    aria-label="Delete feedback"
                    className="text-muted-foreground hover:text-red-600 transition-colors"
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this feedback?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes your submission. This can&apos;t
                      be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDelete(item._id)}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            {item.subject && (
              <p className="text-sm font-medium">{item.subject}</p>
            )}
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {item.message}
            </p>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Feedback</DialogTitle>
          <DialogDescription>
            Report a bug, request a feature, or share an idea — and track what
            you&apos;ve sent.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={setTab}
          className="flex-1 flex flex-col min-h-0"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="submit">Send feedback</TabsTrigger>
            <TabsTrigger value="mine">
              My feedback{myFeedback.length ? ` (${myFeedback.length})` : ''}
            </TabsTrigger>
          </TabsList>

          {/* SUBMIT TAB */}
          <TabsContent
            value="submit"
            className="mt-3 space-y-4 flex-1 overflow-y-auto min-h-0"
          >
            <div className="space-y-1.5">
              <Label htmlFor="feedback-category">Type</Label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as FeedbackCategory)}
              >
                <SelectTrigger id="feedback-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="feedback-subject">Subject (optional)</Label>
              <Input
                id="feedback-subject"
                value={subject}
                maxLength={120}
                placeholder="Short summary"
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="feedback-message">
                Message <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="feedback-message"
                value={message}
                maxLength={4000}
                rows={5}
                placeholder="Tell us what's on your mind…"
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={isLoading || !message.trim()}
              >
                {isLoading ? 'Sending…' : 'Send feedback'}
              </Button>
            </div>
          </TabsContent>

          {/* MY FEEDBACK TAB */}
          <TabsContent
            value="mine"
            className="mt-3 flex-1 overflow-y-auto min-h-0"
          >
            {renderMine()}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
